/**
 * EmployeeSelect — общее поле одиночного выбора сотрудника (centrika-docs, common.md, MR !22,
 * раздел «Одиночный выбор сотрудника»). Применяется ко ВСЕМ полям, где клиент или сотрудник
 * компании выбирает ОДНОГО сотрудника из выпадающего списка; массовый выбор чекбоксами
 * (диалог «Из структуры компании», список сменщиков в карточке сотрудника, окно выбора
 * слушателей обучения) живёт по требованиям своих страниц и сюда не относится.
 *
 * Поле совмещает выпадающий список с поиском:
 * - поиск начинается с ПЕРВОГО введённого символа, фильтр — вхождение подстроки в ПОЛНОЕ ФИО
 *   без учёта регистра; должность, подразделение и прочие данные в поиске НЕ участвуют
 *   (метка опции может их показывать — ищем всё равно только по `fullName`);
 * - при отсутствии совпадений выводится «Сотрудники не найдены»;
 * - после выбора сотрудник становится значением поля, список закрывается, запрос очищается —
 *   при следующем раскрытии виден ПОЛНЫЙ список доступных сотрудников.
 *
 * Служебные опции (их наличие определяет вызывающий экран):
 * - `onAddNew` → первой строкой акцентная опция «+ Добавить сотрудника», НЕПОСРЕДСТВЕННО за ней —
 *   разделительная черта;
 * - `noneOption` → опция отсутствия выбора («Не назначен» / «Без сотрудника» / «Без сменщика»)
 *   после разделительной черты;
 * - список сотрудников идёт сразу после служебных опций, БЕЗ дополнительной черты;
 * - если опции «+ Добавить сотрудника» нет, разделительная черта не рисуется вовсе;
 * - служебные опции видны при ЛЮБОМ поисковом запросе и в фильтрации не участвуют.
 *
 * `groups` — сотрудники несколькими группами с разделителями между ними: группировка страницы
 * (шаг «Ответственный» заказа СОУТ, centrika-docs MR !11 — «члены комиссии» и «остальные
 * сотрудники»). Общее правило служебных опций её не отменяет: разделители групп добавляются
 * ПОСЛЕ служебных опций, к самому списку сотрудников.
 *
 * Реализация — WAI-ARIA combobox по образцу `PositionSelect` (там же — обоснование портала,
 * `pointer-events` внутри Radix Dialog и preventDefault на mousedown): `role="combobox"`,
 * `aria-autocomplete="list"`, ↑/↓ двигают активную строку (с прокруткой в вид) по служебным
 * опциям наравне с сотрудниками, Enter коммитит активную, Esc закрывает. Список рендерится
 * порталом в `document.body` c `position:fixed` — не обрезается `overflow` предка (в частности
 * внутри модального диалога, спека `dialog-portal-layering`).
 */
import { Fragment, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Input } from '@/design-system/components/primitives/input'
import { useComboboxEscape } from './use-combobox-escape'
import { useWorkScaleClass } from '@/design-system/work-scale'
import { cn } from '@/lib/utils'
import { normalizePersonName } from '@/lib/name-case'

/** Текст пустого результата поиска (centrika-docs, common.md, MR !22). */
export const EMPLOYEE_SELECT_EMPTY = 'Сотрудники не найдены'

/** Подпись акцентной опции создания сотрудника (centrika-docs, common.md, MR !22). */
export const EMPLOYEE_SELECT_ADD = '+ Добавить сотрудника'

/** Подсказка режима `allowCreate`, когда введённое ФИО не совпало ни с кем (centrika-docs!100). */
export function employeeSelectNewHint(fullName: string): string {
  return `Новый сотрудник «${fullName}» — будет добавлен при сохранении`
}

export interface EmployeeSelectOption {
  /** Значение поля — идентификатор сотрудника. */
  value: string
  /** Отображаемая метка: ФИО или «должность — ФИО» / «ФИО — подразделение · должность». */
  label: string
  /** Полное ФИО — ЕДИНСТВЕННЫЙ ключ поиска по требованию. */
  fullName: string
}

/** Группа сотрудников; между непустыми группами рисуется разделительная черта. */
export interface EmployeeSelectGroup {
  key: string
  options: EmployeeSelectOption[]
}

/** Опция отсутствия выбранного сотрудника — текст задаёт вызывающее поле. */
export interface EmployeeSelectNoneOption {
  value: string
  label: string
}

export interface EmployeeSelectProps {
  /** Текущее значение: id сотрудника, значение `noneOption` либо пустая строка. */
  value: string
  /** Плоский список сотрудников — сахар над `groups` из одной группы. */
  options?: EmployeeSelectOption[]
  /** Сотрудники группами (разделитель между непустыми группами). Имеет приоритет над `options`. */
  groups?: EmployeeSelectGroup[]
  /** Выбор сотрудника или опции отсутствия — приходит `value` выбранной строки. */
  onSelect: (value: string) => void
  /** Если задан — первой строкой показывается «+ Добавить сотрудника» и разделительная черта. */
  onAddNew?: () => void
  /** Опция отсутствия выбора, если логика поля её допускает. */
  noneOption?: EmployeeSelectNoneOption
  /** Метка текущего значения, если его нет среди опций (замороженное ФИО, «создаётся новый»). */
  valueLabel?: string
  /**
   * Разрешить ввод ФИО НОВОГО сотрудника (centrika-docs!100, паритет с `PositionSelect.allowCreate`):
   * поле принимает свободный текст и не сбрасывает его на blur, а каждое изменение уходит наверх
   * через `onFreeText`. Связывание с существующим сотрудником остаётся ЯВНЫМ выбором строки
   * списка (`onSelect`): совпадение по тексту не подставляется автоматически — ФИО не уникально,
   * и тёзка привязался бы молча. Сам сотрудник создаётся позже, при сохранении формы.
   */
  allowCreate?: boolean
  /** Свободный ввод в режиме `allowCreate` — введённый текст (ФИО нового сотрудника). */
  onFreeText?: (fullName: string) => void
  placeholder?: string
  id?: string
  'aria-label'?: string
  'aria-invalid'?: boolean | undefined
  'aria-describedby'?: string | undefined
  /** Обязательность для вспомогательных технологий: маркер «*» у подписи от них скрыт. */
  'aria-required'?: true | undefined
  invalid?: boolean
  disabled?: boolean
}

/** Строка списка, доступная для выбора (разделители в навигации не участвуют). */
type Row = { kind: 'add'; label: string } | { kind: 'option'; label: string; value: string }

export function EmployeeSelect({
  value,
  options,
  groups,
  onSelect,
  onAddNew,
  noneOption,
  valueLabel,
  allowCreate,
  onFreeText,
  placeholder = 'Выберите сотрудника',
  id,
  invalid,
  disabled,
  ...aria
}: EmployeeSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  // Пользователь печатал в этом раскрытии списка. В режиме `allowCreate` поле открывается с уже
  // введённым ФИО (иначе набранный текст исчезал бы при каждом клике в поле), но фильтровать по
  // нему сразу нельзя: список свернулся бы до совпадений с этим именем, а клиент раскрывает
  // поле как раз чтобы увидеть, кто есть в структуре. Фильтр включается с первым нажатием.
  const [typed, setTyped] = useState(false)
  // Активная строка для клавиатурной навигации (-1 — ничего не подсвечено).
  const [active, setActive] = useState(-1)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const reactId = useId()
  const listboxId = `${reactId}-listbox`
  const rowId = (i: number) => `${reactId}-opt-${i}`

  useEffect(() => () => clearTimeout(blurTimer.current ?? undefined), [])

  const sourceGroups = useMemo<EmployeeSelectGroup[]>(
    () => groups ?? [{ key: 'all', options: options ?? [] }],
    [groups, options],
  )

  // Фильтр по вхождению в ПОЛНОЕ ФИО без учёта регистра, с первого символа запроса.
  const visibleGroups = useMemo(() => {
    const q = allowCreate && !typed ? '' : query.trim().toLowerCase()
    const filtered = q
      ? sourceGroups.map((g) => ({
          key: g.key,
          options: g.options.filter((o) => o.fullName.toLowerCase().includes(q)),
        }))
      : sourceGroups
    return filtered.filter((g) => g.options.length > 0)
  }, [sourceGroups, query, allowCreate, typed])

  const employeesFound = visibleGroups.length > 0

  // Плоский список выбираемых строк — порядок совпадает с визуальным (служебные опции первыми).
  const rows = useMemo<Row[]>(() => {
    const acc: Row[] = []
    if (onAddNew) acc.push({ kind: 'add', label: EMPLOYEE_SELECT_ADD })
    if (noneOption) acc.push({ kind: 'option', label: noneOption.label, value: noneOption.value })
    visibleGroups.forEach((g) =>
      g.options.forEach((o) => acc.push({ kind: 'option', label: o.label, value: o.value })),
    )
    return acc
  }, [onAddNew, noneOption, visibleGroups])

  // Индекс первой строки каждой группы в плоском `rows` — чтобы подсветка и `aria-activedescendant`
  // указывали на тот же id, что и отрисованная строка.
  const serviceRows = (onAddNew ? 1 : 0) + (noneOption ? 1 : 0)
  const groupOffsets = useMemo(() => {
    const offsets: number[] = []
    let acc = serviceRows
    visibleGroups.forEach((g) => {
      offsets.push(acc)
      acc += g.options.length
    })
    return offsets
  }, [visibleGroups, serviceRows])

  // Сброс подсветки при смене запроса/открытия — активная строка не «залипает» вне диапазона.
  useEffect(() => {
    setActive(-1)
  }, [query, open])

  // Отображаемое значение: метка выбранной опции, иначе опции отсутствия, иначе фолбэк.
  // В режиме `allowCreate` свободно введённое ФИО (`valueLabel`) приоритетнее подписи опции
  // отсутствия: пока сотрудник не выбран, поле обязано показывать именно набранный текст.
  const selected = sourceGroups.flatMap((g) => g.options).find((o) => o.value === value)
  const noneSelected = Boolean(noneOption && noneOption.value === value)
  const freeText = allowCreate ? (valueLabel ?? '') : ''
  const display =
    selected?.label ??
    (freeText || (noneSelected ? (noneOption?.label ?? '') : (valueLabel ?? '')))

  // В открытом состоянии инпут показывает вводимый фильтр (выбранное имя остаётся подсказкой
  // в placeholder), в закрытом — выбранное значение.
  const inputValue = open ? query : display
  const visible = open && !disabled

  useLayoutEffect(() => {
    if (!visible) return
    const update = () => {
      const el = anchorRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      setPos({ top: r.bottom + 4, left: r.left, width: r.width })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [visible])

  // Прокрутка активной строки в видимую область при навигации с клавиатуры.
  useEffect(() => {
    if (!visible || active < 0) return
    const el = document.getElementById(rowId(active))
    // scrollIntoView отсутствует в jsdom — вызываем опционально.
    el?.scrollIntoView?.({ block: 'nearest' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, visible])

  function close() {
    setOpen(false)
    setQuery('')
    setActive(-1)
    setTyped(false)
  }

  // Escape закрывает только список, а не модальный диалог вокруг поля — см. докстринг хука
  // (перехват в фазе захвата на window, раньше DismissableLayer у Radix Dialog).
  // Шкала шрифтов портального списка (centrika-docs!31): класс берётся из контекста, а не
  // ставится безусловно — портал живёт в `document.body`, вне DOM-поддерева рабочей области.
  const workScale = useWorkScaleClass()
  useComboboxEscape(visible, anchorRef, close)

  function commit(row: Row) {
    if (blurTimer.current) clearTimeout(blurTimer.current)
    // Требование: после выбора список закрывается, а поисковый запрос очищается — следующее
    // раскрытие показывает полный список.
    close()
    if (row.kind === 'add') onAddNew?.()
    else onSelect(row.value)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (!open) {
          setOpen(true)
          return
        }
        if (rows.length > 0) setActive((a) => (a < 0 ? 0 : Math.min(a + 1, rows.length - 1)))
        break
      case 'ArrowUp':
        e.preventDefault()
        if (open && rows.length > 0) setActive((a) => (a <= 0 ? 0 : a - 1))
        break
      case 'Enter':
        // Коммит только при открытом списке и выбранной активной строке — иначе Enter не
        // перехватывается (не мешаем сабмиту формы).
        if (open && active >= 0 && active < rows.length) {
          e.preventDefault()
          commit(rows[active])
        }
        break
      // Escape здесь НЕ обрабатывается: он перехватывается раньше, в фазе захвата на window
      // (`useComboboxEscape`) — иначе модальный Radix Dialog закрылся бы первым.
      default:
        break
    }
  }

  const activeDescendant = visible && active >= 0 ? rowId(active) : undefined

  /**
   * Одна выбираемая строка списка (служебная опция или сотрудник). Обработчики — на самом
   * `role="option"` (не на вложенной кнопке): фокус остаётся в инпуте, а строка списка — то,
   * что видит скринридер и на что указывает `aria-activedescendant`.
   */
  function renderRow(row: Row, index: number, accent: boolean) {
    return (
      // Клавиатура combobox'а живёт на инпуте (`aria-activedescendant`), а не на опциях: сами
      // опции по паттерну WAI-ARIA не фокусируются и своих keyboard-обработчиков не имеют.
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <li
        key={`${row.kind}-${row.kind === 'add' ? 'add' : row.value}-${index}`}
        id={rowId(index)}
        role="option"
        aria-selected={row.kind === 'option' && row.value === value}
        className={
          'cursor-pointer px-3 py-2 text-14 hover:bg-hover' +
          (accent ? ' font-medium text-accent' : ' text-txt') +
          (index === active ? ' bg-hover' : '')
        }
        // preventDefault на mousedown удерживает фокус в инпуте (портал вне инпута), чтобы
        // blur-таймер не закрыл список до onClick.
        onMouseDown={(e) => e.preventDefault()}
        onMouseEnter={() => setActive(index)}
        onClick={() => commit(row)}
      >
        {row.label}
      </li>
    )
  }

  return (
    <div ref={anchorRef}>
      <Input
        id={id}
        value={inputValue}
        // Открытое поле держит выбранное значение подсказкой: запрос очищен, а видно, кто выбран.
        placeholder={open && display ? display : placeholder}
        invalid={invalid}
        disabled={disabled}
        aria-label={aria['aria-label']}
        aria-invalid={aria['aria-invalid']}
        aria-describedby={aria['aria-describedby']}
        aria-required={aria['aria-required']}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={visible}
        aria-controls={visible ? listboxId : undefined}
        aria-activedescendant={activeDescendant}
        onKeyDown={onKeyDown}
        onChange={(e) => {
          setQuery(e.target.value)
          setTyped(true)
          if (!open) setOpen(true)
          // allowCreate: введённое ФИО сразу уходит наверх — иначе оно потерялось бы на blur.
          if (allowCreate) onFreeText?.(e.target.value)
        }}
        // Раскрытие и по фокусу, и по клику: после выбора мышью фокус остаётся в поле, и без
        // onClick повторное раскрытие было бы недоступно.
        onFocus={(e) => {
          // allowCreate: запрос инициализируется СВОБОДНО ВВЕДЁННЫМ ФИО (не меткой выбранного
          // сотрудника — она вида «ФИО — должность» и в фильтр по ФИО не попала бы, оставив
          // список пустым) и выделяется: при Tab-проходе набранное имя не «исчезает», а ввод
          // сразу заменяет выделенный текст.
          if (allowCreate) {
            setQuery(freeText)
            setTyped(false)
            e.currentTarget.select()
          }
          setOpen(true)
        }}
        onClick={() => setOpen(true)}
        onBlur={() => {
          // Строгий выбор: набранный запрос отбрасывается, значение поля не меняется.
          // allowCreate: текст уже применён наверх (onFreeText) и остаётся в поле как `display`;
          // на уходе с поля ФИО приводится к виду «Иванов Иван Иванович»
          // (names-and-positions-normalization).
          // Текст — набранный в этом раскрытии запрос, иначе уже применённое ФИО (список могли
          // закрыть клавишей Escape до ухода с поля). Если выбран существующий сотрудник, а пользователь
          // ничего не набирал, поле не трогаем: свободный ввод (onFreeText) отвязал бы сотрудника.
          const linked = Boolean(value) && value !== noneOption?.value
          if (allowCreate && (typed || !linked)) {
            const current = open && typed ? query : freeText
            const normalized = normalizePersonName(current)
            if (normalized !== current) {
              if (open && typed) setQuery(normalized)
              onFreeText?.(normalized)
            }
          }
          blurTimer.current = setTimeout(close, 150)
        }}
      />
      {visible && pos
        ? createPortal(
            <ul
              id={listboxId}
              className={cn(
                'ds-surface pointer-events-auto fixed z-popover max-h-56 overflow-auto rounded-8 py-1 shadow-elevated',
                workScale,
              )}
              // pointerEvents inline (не только Tailwind-класс): модальный Radix Dialog ставит
              // pointer-events:none на <body>, портал-потомок его наследует.
              style={{ top: pos.top, left: pos.left, width: pos.width, pointerEvents: 'auto' }}
              role="listbox"
            >
              {onAddNew ? renderRow(rows[0], 0, true) : null}
              {/* Разделительная черта — ТОЛЬКО непосредственно после «+ Добавить сотрудника». */}
              {onAddNew ? <Separator /> : null}
              {noneOption ? renderRow(rows[onAddNew ? 1 : 0], onAddNew ? 1 : 0, false) : null}
              {employeesFound ? (
                visibleGroups.map((g, gi) => (
                  <Fragment key={g.key}>
                    {/* Разделители ГРУПП сотрудников — требование конкретной страницы (MR !11);
                        перед первой группой черты нет: список идёт сразу за служебными опциями. */}
                    {gi > 0 ? <Separator /> : null}
                    {g.options.map((o, oi) =>
                      renderRow(
                        { kind: 'option', label: o.label, value: o.value },
                        groupOffsets[gi] + oi,
                        false,
                      ),
                    )}
                  </Fragment>
                ))
              ) : (
                <li className="px-3 py-2 text-13 text-txt-tertiary">
                  {allowCreate && typed && query.trim()
                    ? employeeSelectNewHint(query.trim())
                    : EMPLOYEE_SELECT_EMPTY}
                </li>
              )}
            </ul>,
            document.body,
          )
        : null}
    </div>
  )
}

/** Разделительная черта списка — тот же вид и `data-testid`, что у `SelectSeparator`. */
function Separator() {
  return <li aria-hidden className="-mx-1 my-1 h-px bg-line" data-testid="select-separator" />
}
